// @ts-check

import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  getUserPreferencesForCurrentUser,
  updateUserPreferencesForCurrentUser,
} from "src/queries/userPreferences.graphql";
import { CACHE_AND_NETWORK_THEN_CACHE_FIRST } from "src/apollo/fetchPolicies";
import useLocalStorage from "src/hooks/useLocalStorage";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import Consts from "../consts";
import Utils from "../utils";

/**
 * Custom hook for managing user preferences via GraphQL.
 */
export const useUserPreferences = () => {
  /** @see user_prefs.PreferencesByScope Each pref layer by scope */
  const [scopedPreferences, setScopedPreferences] = useState(
    Consts.emptyPreferencesCollection
  );
  /** @see user_prefs.Preferences The result of collapsing all pref layers, i.e merging all scoped prefs */
  const [mergedPreferences, setMergedPreferences] = useState(
    Consts.defaultPreferences
  );
  // need this to keep loading in sync with local state (else loading is false before prefs are returned)
  const [isLoading, setIsLoading] = useState(true);

  // cache prefs locally in storage so they are available when loading, do per workspace
  const workspace = useWorkspace();
  const [localMergedPrefs, setLocalMergedPrefs] = useLocalStorage(
    `user_prefs_${workspace.id || "no-id"}`,
    Consts.defaultPreferences
  );

  const { data, loading } = useQuery(getUserPreferencesForCurrentUser, {
    ...CACHE_AND_NETWORK_THEN_CACHE_FIRST,
  });

  const [updateUserPreferences] = useMutation(
    updateUserPreferencesForCurrentUser
  );

  useEffect(() => {
    const prefs = data?.userPreferences || [];
    if (prefs.length > 0) {
      setScopedPreferences(Utils.getScopedPrefsFromGqlPrefs(prefs));

      const mergedPrefs = Utils.getMergedPrefsFromGqlPrefs(prefs);
      setMergedPreferences(mergedPrefs);
      setLocalMergedPrefs(mergedPrefs);
    }
  }, [data, setLocalMergedPrefs]);

  // the gql 'loading' is ahead of internal state, this puts it in sync
  useEffect(() => {
    if (!loading) {
      setIsLoading(false);
    }
  }, [loading]);

  // memo each scopes prefs
  const [
    globalAllUsersPreferences,
    globalUserPreferences,
    workspaceAllUsersPreferences,
    workspaceUserPreferences,
  ] = useMemo(() => {
    return [
      scopedPreferences[Consts.Scopes.GLOBAL_ALL_USERS],
      scopedPreferences[Consts.Scopes.GLOBAL_USER],
      scopedPreferences[Consts.Scopes.WORKSPACE_ALL_USERS],
      scopedPreferences[Consts.Scopes.WORKSPACE_USER],
    ];
  }, [scopedPreferences]);

  const updatePreferencesForAllScopes = useCallback(
    /**
     * Updates the user preferences for all provided scopes.
     * @param {user_prefs.PreferencesByScope} scopedPrefs
     * @returns {Promise<boolean>}
     */
    async (scopedPrefs) => {
      const { error } = await updateUserPreferences({
        variables: {
          preferences: Utils.getGqlUpdatesFromPrefsCollection(scopedPrefs),
        },
        update: (cache, { data: updateData }) => {
          // update the 'get' cache so consumers get the update without having to refetch
          /** @type {{ userPreferences?: user_prefs.gql.UserPreferences[] } | null} */
          const cachedPrefs = cache.readQuery({
            query: getUserPreferencesForCurrentUser,
          });
          const existingPrefs = cachedPrefs?.userPreferences || [];

          const updatedPrefs = Utils.getPrefsForCacheWrite(
            existingPrefs,
            updateData?.userPreferences || []
          );

          cache.writeQuery({
            query: getUserPreferencesForCurrentUser,
            data: {
              userPreferences: updatedPrefs,
            },
          });
        },
      });

      if (error) {
        return false;
      }

      return true;
    },
    [updateUserPreferences]
  );

  const updatePreferencesForScope = useCallback(
    /**
     * @param {user_prefs.Scope} scope
     * @param {user_prefs.Preferences} preferences
     * @returns {Promise<boolean>}
     */
    async (scope, preferences) => {
      return updatePreferencesForAllScopes({
        [scope]: preferences,
      });
    },
    [updatePreferencesForAllScopes]
  );

  const updatePreferencesForWorkspaceUser = useCallback(
    /**
     * @param {user_prefs.Preferences} preferences
     * @returns {Promise<boolean>}
     */
    (preferences) =>
      updatePreferencesForScope(Consts.Scopes.WORKSPACE_USER, preferences),
    [updatePreferencesForScope]
  );

  const updatePreferencesForWorkspaceAllUsers = useCallback(
    /**
     * @param {user_prefs.Preferences} preferences
     * @returns {Promise<boolean>}
     */
    (preferences) =>
      updatePreferencesForScope(Consts.Scopes.WORKSPACE_ALL_USERS, preferences),
    [updatePreferencesForScope]
  );

  const updatePreferencesForGlobalUser = useCallback(
    /**
     * @param {user_prefs.Preferences} preferences
     * @returns {Promise<boolean>}
     */
    (preferences) =>
      updatePreferencesForScope(Consts.Scopes.GLOBAL_USER, preferences),
    [updatePreferencesForScope]
  );

  const updatePreferencesForGlobalAllUsers = useCallback(
    /**
     * @param {user_prefs.Preferences} preferences
     * @returns {Promise<boolean>}
     */
    (preferences) =>
      updatePreferencesForScope(Consts.Scopes.GLOBAL_ALL_USERS, preferences),
    [updatePreferencesForScope]
  );

  return {
    loading: isLoading,
    mergedPreferences: isLoading ? localMergedPrefs : mergedPreferences,
    scopedPreferences,
    globalAllUsersPreferences,
    globalUserPreferences,
    workspaceAllUsersPreferences,
    workspaceUserPreferences,
    updatePreferencesForAllScopes,
    updatePreferencesForScope,
    updatePreferencesForWorkspaceUser,
    updatePreferencesForWorkspaceAllUsers,
    updatePreferencesForGlobalUser,
    updatePreferencesForGlobalAllUsers,
  };
};
